import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { CalendarViews, DayComposite } from '../../calendar-types';
import { DayCompositeConverter } from '../../converter/day-composite-converter';
import { DateTimeHelper } from '../../util/date-time-helper';
import { CalendarService } from '../../services/calendar.service';
import { DayOrWeekCalendarModel } from '../../../../core/models/calendar/day-or-week-calendar.model';
import {
  CalendarHeaderCSSId,
  FixIntervalCalendarContainerExpandable,
  FixIntervalCalendarContainerId,
  FixIntervalContainerBottomSpacing,
  FixIntervalContainerMinimumHeight,
  FixIntervalContainerTopSpacing,
  FixIntervalTimeBlockHeightPx,
  HorizontalTimeBlockGapPx,
} from '../../util/calendar-style.constants';
import { CalendarScrollbarService } from '../../services/calendar-scrollbar.service';

import { Subject, take } from 'rxjs';
import { assert } from '../../../../core/assert/assert';
import { MonthCalendarModel } from '../../../../core/models/calendar/month-calendar.model';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';

@Injectable()
export class CalendarHeaderService {
  public fixIntervalContainerExpanded = false;
  public finishedHeightToggling$ = new Subject();
  private collapsedFixIntervalHeight: number;
  private expandedFixIntervalHeight: number;
  private yScrollTrack: HTMLDivElement;
  private readonly maxFixIntervalRowsCollapsed = 3;
  private readonly maxFixIntervalRowsExpanded = 6;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly calendarService: CalendarService,
    private readonly calendarScrollbarService: CalendarScrollbarService,
    private readonly renderer: ShiveRenderService,
  ) {}

  public reRenderFixIntervalContainerDays(): DayComposite[] {
    const calendarModel = this.calendarService.model;
    const calendarView = calendarModel.calendarViewMode;
    let containerDays: DayComposite[];

    if (calendarView === CalendarViews.DayGrid) {
      containerDays = DayCompositeConverter.toDayComposites(
        [calendarModel.calendarProperties.visibleStartDate],
        calendarModel.holidays,
      );
      return containerDays;
    }

    if (calendarView === CalendarViews.WeekGrid) {
      const visibleStartDate = calendarModel.calendarProperties.visibleStartDate;
      const visibleEndDate = calendarModel.calendarProperties.visibleEndDate;
      const days = DateTimeHelper.eachDayOfInterval(visibleStartDate, visibleEndDate);
      containerDays = DayCompositeConverter.toDayComposites(
        days,
        calendarModel.holidays,
        visibleStartDate,
        visibleEndDate,
      );
      return containerDays;
    }

    if (calendarView === CalendarViews.MonthGrid) {
      const startOfWeek = DateTimeHelper.startOfWeek(new Date(), {
        weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
      });
      const endOfWeek = DateTimeHelper.endOfWeek(new Date(), {
        weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
      });
      containerDays = DayCompositeConverter.toDayComposites(
        DateTimeHelper.eachDayOfInterval(startOfWeek, endOfWeek),
        calendarModel.holidays,
      );
      return containerDays;
    }
  }

  public initDayOrWeekViewFixLaneDayContainer(): void {
    // Initially hide y-track
    this.yScrollTrack = this.document.querySelector('.scrollbar-track-y');
    assert(typeof this.yScrollTrack !== 'undefined', 'y-scroll track not found.');
    this.renderer.removeClass(this.yScrollTrack, 'show');
  }

  /**
   * Calculate the height of the full day calendar container and set it accordingly.
   * If there is no full day time block, set a minimum height.
   */
  public renderDayOrWeekViewFixLaneDayContainer(): void {
    this.setDayOrWeekFixIntervalContainerHeight();

    const fixIntervalHTMLTable = document.querySelector(FixIntervalCalendarContainerId);
    const model = this.calendarService.model as DayOrWeekCalendarModel;
    const maxExpandedHeight = this.calculateDayOrWeekViewFixIntervalContainerHeight(
      this.maxFixIntervalRowsExpanded,
    );

    // Set the max expanded height. If higher, a scroll bar is displayed.
    const calendarHeaderScrollContainer = fixIntervalHTMLTable.querySelector(CalendarHeaderCSSId);
    this.renderer.setStyle(calendarHeaderScrollContainer, 'max-height', `${maxExpandedHeight}px`);

    const highestParallelIndex = model.fixIntervalCalendarModel.highestParallelIndex;

    if (highestParallelIndex > this.maxFixIntervalRowsCollapsed) {
      this.renderer.addClass(fixIntervalHTMLTable, FixIntervalCalendarContainerExpandable);
    } else {
      this.renderer.removeClass(fixIntervalHTMLTable, FixIntervalCalendarContainerExpandable);
    }
  }

  public toggleDayOrWeekViewFixLaneDayContainer(show?: boolean): void {
    if (typeof show !== 'undefined') {
      this.fixIntervalContainerExpanded = show;
    } else {
      this.fixIntervalContainerExpanded = !this.fixIntervalContainerExpanded;
    }
    this.setDayOrWeekFixIntervalContainerHeight();
    this.calendarScrollbarService.calendarHeaderScrollbar.disable(
      !this.fixIntervalContainerExpanded,
    );

    this.finishedHeightToggling$.pipe(take(1)).subscribe(() => {
      if (this.fixIntervalContainerExpanded) {
        this.renderer.addClass(this.yScrollTrack, 'show');
      } else {
        this.calendarScrollbarService.calendarHeaderScrollbar.scrollToTop(
          this.expandedFixIntervalHeight,
        );
        this.renderer.removeClass(this.yScrollTrack, 'show');
      }
    });
  }

  /**
   * Set height of full day item container
   */
  public setDayOrWeekFixIntervalContainerHeight(): void {
    const model = this.calendarService.model as DayOrWeekCalendarModel;
    const fixIntervalGeometryData = model.fixIntervalCalendarModel.geometryData;
    const fixIntervalHTMLTimeBlockContainer =
      fixIntervalGeometryData.calendarBodyElementRef.nativeElement;

    assert(
      typeof fixIntervalHTMLTimeBlockContainer !== 'undefined',
      'Fix interval html container not defined.',
    );

    const highestParallelIndex = model.fixIntervalCalendarModel.highestParallelIndex;
    const parallelRows = highestParallelIndex === 0 ? 0 : highestParallelIndex;
    const collapsedRows =
      parallelRows > this.maxFixIntervalRowsCollapsed
        ? this.maxFixIntervalRowsCollapsed
        : parallelRows;
    this.collapsedFixIntervalHeight =
      this.calculateDayOrWeekViewFixIntervalContainerHeight(collapsedRows);
    this.expandedFixIntervalHeight =
      this.calculateDayOrWeekViewFixIntervalContainerHeight(parallelRows);

    let height: number;
    if (this.fixIntervalContainerExpanded) {
      height = this.expandedFixIntervalHeight;
    } else {
      height = this.collapsedFixIntervalHeight;
    }
    fixIntervalGeometryData.calendarBodyHeight = height;
    fixIntervalGeometryData.calendarBodyVisibleHeight = height;

    this.renderer.setStyle(
      fixIntervalHTMLTimeBlockContainer,
      'height',
      `${fixIntervalGeometryData.calendarBodyHeight}px`,
    );
  }

  public getMonthHeaderFixIntervalComposites(): DayComposite[] {
    const calendarModel = this.calendarService.model as MonthCalendarModel;
    const startOfWeek = DateTimeHelper.startOfWeek(
      calendarModel.calendarProperties.visibleStartDate,
      {
        weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
      },
    );
    const endOfWeek = DateTimeHelper.endOfWeek(startOfWeek, {
      weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
    });

    const dayComposites = calendarModel.calendarProperties.days;
    let i = dayComposites.findIndex((dayComposite) =>
      DateTimeHelper.isSameDay(startOfWeek, dayComposite.theDate),
    );
    assert(i >= 0, 'Index is negative.');
    let curDay = dayComposites[i];
    const fixIntervalComposites = [];

    while (!DateTimeHelper.isSameDay(curDay.theDate, DateTimeHelper.addDays(endOfWeek, 1))) {
      fixIntervalComposites.push(dayComposites[i]);
      curDay = dayComposites[++i];
    }

    return fixIntervalComposites;
  }

  private calculateDayOrWeekViewFixIntervalContainerHeight(rowCount: number): number {
    // If there are no full day time blocks in the full day item container, return the default height
    if (rowCount === 0) {
      return FixIntervalContainerMinimumHeight;
    }

    return (
      FixIntervalTimeBlockHeightPx * rowCount +
      HorizontalTimeBlockGapPx * rowCount +
      FixIntervalContainerTopSpacing +
      FixIntervalContainerBottomSpacing
    );
  }
}
